/** The campaign's headline: are we going to make it?
 *
 *  Five states, deliberately. Asked 2026-09-27 while the off-pace
 *  design was being built — "what will happen when we are on track,
 *  will the style be changed?" It must: a banner that is only ever red
 *  is one people learn to scroll past, and one that disappears when
 *  things are fine reads as a page that failed to load.
 *
 *  So every state says something, and the two that should NOT make a
 *  claim — a campaign with no deadline, and one with too little history
 *  to project — say that instead of guessing.
 */
import { useTranslation } from "react-i18next";

import type { Goal } from "./campaignGoal";

const TONE = {
  off_pace: {
    box: "border-cvp-danger/40 bg-gradient-to-br from-cvp-danger/15 via-cvp-danger/5 to-transparent",
    rule: "bg-cvp-danger",
    tag: "border-cvp-danger/45 bg-cvp-danger/15 text-red-300",
  },
  on_pace: {
    box: "border-nb-mint/35 bg-gradient-to-br from-nb-mint/12 via-nb-mint/4 to-transparent",
    rule: "bg-nb-mint",
    tag: "border-nb-mint/40 bg-nb-mint/10 text-nb-mint",
  },
  complete: {
    box: "border-nb-mint/35 bg-gradient-to-br from-nb-mint/12 via-nb-mint/4 to-transparent",
    rule: "bg-nb-mint",
    tag: "border-nb-mint/40 bg-nb-mint/10 text-nb-mint",
  },
  too_early: {
    box: "border-nb-border bg-nb-surface",
    rule: "bg-nb-text-3",
    tag: "border-nb-border bg-nb-bg/50 text-nb-text-3",
  },
  no_deadline: {
    box: "border-nb-border bg-nb-surface",
    rule: "bg-nb-text-3",
    tag: "border-nb-border bg-nb-bg/50 text-nb-text-3",
  },
} as const;

export function CampaignVerdict({
  goal,
  capacityPerDay,
}: {
  goal: Goal;
  capacityPerDay?: number;
}) {
  const { t } = useTranslation();
  const tone = TONE[goal.status];

  /** The one number the banner leads with, and what it is. */
  const lead =
    goal.status === "complete"
      ? { value: "100%", unit: t("campaigns.goal.unitComplete") }
      : goal.status === "off_pace" || goal.status === "on_pace"
        ? {
            value: Number.isFinite(goal.neededPerDay)
              ? String(goal.neededPerDay)
              : "—",
            unit: t("campaigns.goal.unitPerDay"),
          }
        : { value: String(goal.remaining), unit: t("campaigns.goal.unitLeft") };

  return (
    <section
      className={`relative overflow-hidden rounded-xl border p-4 ${tone.box}`}
    >
      <span
        aria-hidden="true"
        className={`absolute inset-y-0 left-0 w-[3px] ${tone.rule}`}
      />
      <div className="mb-2 flex items-center gap-2">
        <span
          className={`rounded border px-1.5 py-1 font-mono text-[10px] uppercase tracking-widest ${tone.tag}`}
        >
          {t(`campaigns.goal.tag_${goal.status}`)}
        </span>
      </div>

      <p className="font-mono text-3xl font-bold tabular-nums">
        {lead.value}
        <span className="ml-1.5 font-sans text-sm font-medium text-nb-text-3">
          {lead.unit}
        </span>
      </p>

      <p className="mt-1.5 text-sm text-nb-text-2">
        {t(`campaigns.goal.body_${goal.status}`, {
          achieved: goal.achievedPerDay,
          needed: Number.isFinite(goal.neededPerDay) ? goal.neededPerDay : "—",
          remaining: goal.remaining,
          days: goal.workingDaysLeft,
          factor: Number.isFinite(goal.factor) ? goal.factor : "—",
        })}
      </p>

      {/* A's rail rows: the three facts that make the headline
          checkable rather than asserted. */}
      {(goal.status === "off_pace" || goal.status === "on_pace") && (
        <dl className="mt-3 flex flex-col gap-1 border-t border-nb-border/60 pt-2.5 text-xs">
          <SummaryRow
            label={t("campaigns.goal.remaining")}
            value={goal.remaining.toLocaleString()}
          />
          <SummaryRow
            label={t("campaigns.goal.workingDaysLeft")}
            value={String(goal.workingDaysLeft)}
          />
          {goal.daysAtCurrentRate !== null && (
            <SummaryRow
              label={t("campaigns.goal.finishAtThisRate")}
              value={t("campaigns.goal.workingDays", {
                count: goal.daysAtCurrentRate,
              })}
              tone={goal.status === "off_pace" ? "text-red-300" : undefined}
            />
          )}
        </dl>
      )}

      {/* Said only when it is true and useful: that the roster could
          absorb the required rate, so the constraint is bookings. */}
      {goal.status === "off_pace" && goal.capacityIsEnough === true && (
        <p className="mt-2 text-xs text-nb-text-3">
          {t("campaigns.goal.capacityNote", { capacity: capacityPerDay ?? 0 })}
        </p>
      )}

      {(goal.status === "off_pace" || goal.status === "on_pace") && (
        <dl className="mt-3 flex flex-col gap-1.5">
          <Row
            label={t("campaigns.goal.achieved")}
            value={`${goal.achievedPerDay}`}
            pct={pctOf(goal.achievedPerDay, capacityPerDay)}
            bar="bg-nb-mint"
          />
          <Row
            label={t("campaigns.goal.needed")}
            value={
              Number.isFinite(goal.neededPerDay) ? `${goal.neededPerDay}` : "—"
            }
            pct={pctOf(goal.neededPerDay, capacityPerDay)}
            bar={goal.status === "off_pace" ? "bg-cvp-danger" : "bg-nb-mint"}
          />
          {capacityPerDay !== undefined && capacityPerDay > 0 && (
            <Row
              label={t("campaigns.goal.capacity")}
              value={`${capacityPerDay}`}
              pct={100}
              bar="bg-nb-mint/25"
            />
          )}
        </dl>
      )}

      {goal.status === "off_pace" && !goal.tooEarly && (
        <p className="mt-2 text-[11px] text-nb-text-3">
          {t("campaigns.goal.projectionCaveat", {
            days: goal.workingDaysElapsed,
          })}
        </p>
      )}
    </section>
  );
}

/** Bars are scaled against capacity so achieved and needed are directly
 *  comparable. Without a capacity the biggest of the two sets the scale. */
function pctOf(value: number, capacity?: number): number {
  if (!Number.isFinite(value)) return 100;
  const denom = capacity && capacity > 0 ? capacity : value || 1;
  return Math.max(2, Math.min(100, (value / denom) * 100));
}

function SummaryRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-nb-text-3">{label}</dt>
      <dd className={`font-mono tabular-nums ${tone ?? "text-nb-text"}`}>
        {value}
      </dd>
    </div>
  );
}

function Row({
  label,
  value,
  pct,
  bar,
}: {
  label: string;
  value: string;
  pct: number;
  bar: string;
}) {
  return (
    <div className="grid grid-cols-[5.5rem_1fr_3rem] items-center gap-2 text-xs">
      <dt className="text-nb-text-3">{label}</dt>
      <dd className="h-2.5 overflow-hidden rounded-full border border-nb-border bg-nb-bg/70">
        <span className={`block h-full rounded-full ${bar}`} style={{ width: `${pct}%` }} />
      </dd>
      <dd className="text-right font-mono font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
